// ============================================================================
// STUDIO CALENDAR — monthly room booking calendar for Tier 1 / system admins
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
  CalendarRange, ChevronLeft, ChevronRight, Loader, AlertCircle,
  X, User, Tag, Building2, Target, Clock, MapPin, Ban, Users, Pencil, Check,
  Download
} from 'lucide-react';
import {
  listAllBookings,
  listBlockedDays,
  setBlockedDay,
  removeBlockedDay,
  updateBookingMetadata,
  StudioBooking,
  BlockedDay,
  BookingStatus,
  DATE_TYPE_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  TIER_LABELS
} from '../services/studio-bookings';
import { listAllRoomsWithStatus, RoomWithAvailability } from '../services/rooms';

// ============================================================================
// Irish public holidays helper
// ============================================================================

function easterSunday(year: number): Date {
  // Anonymous Gregorian algorithm
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 1-indexed
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function firstMondayOf(year: number, month: number): Date {
  const d = new Date(year, month - 1, 1);
  const dow = d.getDay(); // 0=Sun
  const diff = dow === 1 ? 0 : dow === 0 ? 1 : 8 - dow;
  return new Date(year, month - 1, 1 + diff);
}

function lastMondayOf(year: number, month: number): Date {
  const lastDay = new Date(year, month, 0); // last day of month
  const dow = lastDay.getDay();
  const diff = dow === 1 ? 0 : dow === 0 ? 6 : dow - 1;
  return new Date(year, month - 1, lastDay.getDate() - diff);
}

function irishPublicHolidays(year: number): Set<string> {
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const holidays = new Set<string>();

  // Fixed
  holidays.add(`${year}-01-01`); // New Year's Day
  holidays.add(`${year}-03-17`); // St. Patrick's Day
  holidays.add(`${year}-12-25`); // Christmas Day
  holidays.add(`${year}-12-26`); // St. Stephen's Day

  // St. Brigid's Day: first Monday in February OR Feb 1 if it's a Friday
  const feb1 = new Date(year, 1, 1);
  if (feb1.getDay() === 5) {
    holidays.add(fmt(feb1));
  } else {
    holidays.add(fmt(firstMondayOf(year, 2)));
  }

  // Easter Monday
  const easter = easterSunday(year);
  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);
  holidays.add(fmt(easterMonday));

  // May Day: first Monday in May
  holidays.add(fmt(firstMondayOf(year, 5)));

  // June Bank Holiday: first Monday in June
  holidays.add(fmt(firstMondayOf(year, 6)));

  // August Bank Holiday: first Monday in August
  holidays.add(fmt(firstMondayOf(year, 8)));

  // October Bank Holiday: last Monday in October
  holidays.add(fmt(lastMondayOf(year, 10)));

  return holidays;
}

// ============================================================================
// Date helpers
// ============================================================================

function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseYMD(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isWeekday(d: Date): boolean {
  const dow = d.getDay();
  return dow !== 0 && dow !== 6;
}

function irishWorkingDaysInMonth(year: number, month: number): number {
  const holidays = irishPublicHolidays(year);
  let count = 0;
  const d = new Date(year, month - 1, 1);
  while (d.getMonth() === month - 1) {
    if (isWeekday(d) && !holidays.has(toYMD(d))) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

function workingDaysInRange(start: string, end: string, year: number, month: number): number {
  const holidays = irishPublicHolidays(year);
  const from = parseYMD(start);
  const to = parseYMD(end);
  let count = 0;
  const d = new Date(from);
  while (d <= to) {
    if (isWeekday(d) && !holidays.has(toYMD(d)) && d.getFullYear() === year && d.getMonth() === month - 1) {
      count++;
    }
    d.setDate(d.getDate() + 1);
  }
  return count;
}

// ============================================================================
// Status chip colours
// ============================================================================

const CHIP_COLORS: Record<BookingStatus, string> = {
  approved:  'bg-green-100 text-green-800 border border-green-200',
  pending:   'bg-amber-100 text-amber-800 border border-amber-200',
  rejected:  'bg-red-100 text-red-700 border border-red-200 opacity-60',
  cancelled: 'bg-slate-100 text-slate-500 border border-slate-200 opacity-60'
};

const STATUS_DOT: Record<BookingStatus, string> = {
  pending:   'bg-amber-400',
  approved:  'bg-green-500',
  rejected:  'bg-red-500',
  cancelled: 'bg-slate-400'
};

function StatusBadge({ status }: { status: BookingStatus }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[status]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
      {STATUS_LABELS[status]}
    </span>
  );
}

// ============================================================================
// Booking detail modal
// ============================================================================

function BookingModal({ booking: initialBooking, onClose }: { booking: StudioBooking; onClose: () => void }) {
  const [booking, setBooking] = useState(initialBooking);
  const [editingCounts, setEditingCounts] = useState(false);
  const [customers, setCustomers] = useState(initialBooking.numberOfCustomers?.toString() ?? '');
  const [attendees, setAttendees] = useState(initialBooking.numberOfAttendees?.toString() ?? '');
  const [saving, setSaving] = useState(false);

  const customerStatusLabel = booking.customerStatus === 'internal' ? 'Internal'
    : booking.customerStatus === 'new' ? 'New customer'
    : 'Existing customer';

  const billingLabel = booking.billingType === 'investment' ? 'Investment'
    : booking.billingType === 'na' ? 'N/A'
    : 'Chargeable';

  const customerCountLabel = booking.numberOfCustomers !== undefined
    ? `${booking.numberOfCustomers} ${booking.numberOfCustomers === 1 ? 'customer' : 'customers'}`
    : null;
  const attendeeCountLabel = booking.numberOfAttendees !== undefined
    ? `${booking.numberOfAttendees} ${booking.numberOfAttendees === 1 ? 'attendee' : 'attendees'}`
    : null;
  const countsLabel = [customerCountLabel, attendeeCountLabel].filter(Boolean).join(' · ');

  async function saveCounts() {
    setSaving(true);
    try {
      const updates: { numberOfCustomers?: number; numberOfAttendees?: number } = {};
      if (customers !== '') updates.numberOfCustomers = Number(customers);
      if (attendees !== '') updates.numberOfAttendees = Number(attendees);
      const updated = await updateBookingMetadata(booking.bookingId, updates);
      setBooking(updated);
      setEditingCounts(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg border border-[#e2e8f0]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
          <div className="flex items-center gap-3">
            <StatusBadge status={booking.status} />
            <h2 className="font-bold text-navy">{booking.customer}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex gap-2.5 text-sm">
            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <span className="text-navy">{booking.roomName} · {DATE_TYPE_LABELS[booking.dateType]}</span>
          </div>
          <div className="flex gap-2.5 text-sm">
            <Clock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <span className="text-navy">
              {booking.startDate === booking.endDate
                ? booking.startDate
                : `${booking.startDate} → ${booking.endDate}`}
              {' · '}{booking.startTime}–{booking.endTime}
            </span>
          </div>
          <div className="flex gap-2.5 text-sm">
            <User className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <span className="text-navy">
              {booking.facilitatorName}
              <span className="ml-1.5 text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">
                {TIER_LABELS[booking.facilitatorTier]}
              </span>
            </span>
          </div>
          <div className="flex gap-2.5 text-sm">
            <Tag className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <span className="text-navy">{booking.eventTypeName}</span>
          </div>
          <div className="flex gap-2.5 text-sm">
            <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <span className="text-navy">{customerStatusLabel} · {billingLabel}</span>
          </div>

          {/* Attendee counts — inline editable */}
          <div className="flex gap-2.5 text-sm items-start">
            <Users className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            {editingCounts ? (
              <div className="flex items-center gap-2 flex-1">
                <div className="flex flex-col gap-1 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-24">Customers</span>
                    <input
                      type="number"
                      min={0}
                      value={customers}
                      onChange={e => setCustomers(e.target.value)}
                      className="w-20 border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-24">Attendees</span>
                    <input
                      type="number"
                      min={0}
                      value={attendees}
                      onChange={e => setAttendees(e.target.value)}
                      className="w-20 border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                </div>
                <button
                  onClick={saveCounts}
                  disabled={saving}
                  className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setEditingCounts(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-navy">
                  {countsLabel || <span className="text-slate-400 italic">No counts set</span>}
                </span>
                <button
                  onClick={() => setEditingCounts(true)}
                  className="ml-auto p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-2.5 text-sm">
            <Target className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <span className="text-navy">{booking.goal}</span>
          </div>
          {booking.reviewNotes && (
            <div className="p-3 rounded-xl bg-slate-50 border border-[#e2e8f0] mt-2">
              <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Review notes</p>
              <p className="text-sm text-navy">{booking.reviewNotes}</p>
              {booking.reviewedByName && (
                <p className="text-xs text-slate-400 mt-1">— {booking.reviewedByName}</p>
              )}
            </div>
          )}
          <p className="text-xs text-slate-400 pt-1">
            Requested by {booking.requestedByName}
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CSV export helpers
// ============================================================================

function escapeCsv(val: string | number | undefined | null): string {
  if (val === undefined || val === null) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function bookingsToCsv(rows: StudioBooking[]): string {
  const headers = [
    'Booking ID', 'Room', 'Customer', 'Customer Status', 'Billing Type',
    'Date Type', 'Start Date', 'End Date', 'Start Time', 'End Time', 'Status',
    'Facilitator', 'Facilitator Tier', 'Event Type',
    'Number of Customers', 'Number of Attendees',
    'Goal', 'Requested By', 'Requested By Email',
    'Reviewed By', 'Review Notes', 'Created At',
  ];
  const lines = [headers.join(',')];
  for (const b of rows) {
    lines.push([
      b.bookingId, b.roomName, b.customer,
      b.customerStatus, b.billingType,
      b.dateType, b.startDate, b.endDate, b.startTime, b.endTime,
      b.status, b.facilitatorName, b.facilitatorTier, b.eventTypeName,
      b.numberOfCustomers, b.numberOfAttendees,
      b.goal, b.requestedByName, b.requestedByEmail,
      b.reviewedByName, b.reviewNotes, b.createdAt,
    ].map(escapeCsv).join(','));
  }
  return lines.join('\n');
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ============================================================================
// Export CSV modal
// ============================================================================

interface ExportCsvModalProps {
  bookings: StudioBooking[];
  rooms: RoomWithAvailability[];
  defaultFrom: string;
  defaultTo: string;
  onClose: () => void;
}

function ExportCsvModal({ bookings, rooms, defaultFrom, defaultTo, onClose }: ExportCsvModalProps) {
  const [selectedRoomIds, setSelectedRoomIds] = useState<Set<string>>(new Set(['all']));
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [statusFilter, setStatusFilter] = useState<'active' | 'approved' | 'pending' | 'all'>('active');

  function toggleRoom(id: string) {
    setSelectedRoomIds(prev => {
      const next = new Set(prev);
      if (id === 'all') {
        return new Set(['all']);
      }
      next.delete('all');
      if (next.has(id)) {
        next.delete(id);
        if (next.size === 0) next.add('all');
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const filtered = useMemo(() => {
    return bookings.filter(b => {
      if (!selectedRoomIds.has('all') && !selectedRoomIds.has(b.roomId)) return false;
      if (statusFilter === 'active' && !['pending', 'approved'].includes(b.status)) return false;
      if (statusFilter === 'approved' && b.status !== 'approved') return false;
      if (statusFilter === 'pending' && b.status !== 'pending') return false;
      return b.startDate <= toDate && b.endDate >= fromDate;
    });
  }, [bookings, selectedRoomIds, fromDate, toDate, statusFilter]);

  function handleExport() {
    const csv = bookingsToCsv(filtered);
    const roomLabel = selectedRoomIds.has('all') ? 'all-rooms'
      : Array.from(selectedRoomIds).map(id => rooms.find(r => r.roomId === id)?.name ?? id).join('+').replace(/\s+/g, '-').toLowerCase();
    const filename = `bookings-${roomLabel}-${fromDate}-to-${toDate}.csv`;
    downloadCsv(csv, filename);
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md border border-[#e2e8f0]"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
          <div className="flex items-center gap-2.5">
            <Download className="w-5 h-5 text-primary" />
            <h2 className="font-bold text-navy">Export Bookings as CSV</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Date range */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Date range</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
              <span className="text-slate-400 text-sm">to</span>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={e => setToDate(e.target.value)}
                className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>
          </div>

          {/* Room selection */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Rooms</label>
            <div className="space-y-1.5 max-h-44 overflow-y-auto">
              <label className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedRoomIds.has('all')}
                  onChange={() => toggleRoom('all')}
                  className="w-4 h-4 accent-primary rounded"
                />
                <span className="text-sm font-medium text-navy">All rooms</span>
              </label>
              {rooms.map(r => (
                <label key={r.roomId} className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!selectedRoomIds.has('all') && selectedRoomIds.has(r.roomId)}
                    onChange={() => toggleRoom(r.roomId)}
                    className="w-4 h-4 accent-primary rounded"
                  />
                  <span className="text-sm text-navy">{r.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Status filter */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Status</label>
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden bg-white">
              {([
                ['active', 'Active'],
                ['approved', 'Approved'],
                ['pending', 'Pending'],
                ['all', 'All'],
              ] as const).map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setStatusFilter(val)}
                  className={`flex-1 px-3 py-2 text-xs font-semibold transition-all ${
                    statusFilter === val ? 'bg-primary text-white' : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Preview count */}
          <p className="text-xs text-slate-400 text-center">
            {filtered.length} {filtered.length === 1 ? 'booking' : 'bookings'} will be exported
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 px-5 pb-5">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-[#e2e8f0] rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={filtered.length === 0}
            className="flex-1 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download CSV
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Calendar grid
// ============================================================================

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function buildCalendarGrid(year: number, month: number): Date[] {
  // Monday-first grid
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);

  // Monday=0 offset
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6; // Sunday → 6

  const days: Date[] = [];

  // Leading days from previous month
  for (let i = startOffset; i > 0; i--) {
    const d = new Date(firstDay);
    d.setDate(d.getDate() - i);
    days.push(d);
  }

  // Days in current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(new Date(year, month - 1, i));
  }

  // Trailing days to complete grid (always 6 rows = 42 cells)
  while (days.length < 42) {
    const d = new Date(days[days.length - 1]);
    d.setDate(d.getDate() + 1);
    days.push(d);
  }

  return days;
}

// ============================================================================
// Main page
// ============================================================================

type StatusFilterValue = 'active' | 'all' | 'approved' | 'pending';

export default function StudioCalendar() {
  const [bookings, setBookings] = useState<StudioBooking[]>([]);
  const [rooms, setRooms] = useState<RoomWithAvailability[]>([]);
  const [blockedDays, setBlockedDays] = useState<BlockedDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [selectedRoomId, setSelectedRoomId] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('active');
  const [selectedBooking, setSelectedBooking] = useState<StudioBooking | null>(null);

  const [showExportModal, setShowExportModal] = useState(false);

  // Block day toggle state
  const [blockingDate, setBlockingDate] = useState<string | null>(null); // date being toggled
  const [blockReason, setBlockReason] = useState('');
  const [blockSaving, setBlockSaving] = useState(false);

  // Initial load of bookings + rooms
  useEffect(() => {
    Promise.all([listAllBookings(), listAllRoomsWithStatus()])
      .then(([b, r]) => { setBookings(b); setRooms(r); })
      .catch(() => setError('Failed to load bookings'))
      .finally(() => setLoading(false));
  }, []);

  // Load blocked days whenever month/year changes
  useEffect(() => {
    listBlockedDays(year, month)
      .then(setBlockedDays)
      .catch(() => {}); // non-fatal
  }, [year, month]);

  const blockedDaySet = useMemo(
    () => new Set(blockedDays.map(d => d.date)),
    [blockedDays]
  );

  function prevMonth() {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  }

  function nextMonth() {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  }

  async function handleDayClick(ymd: string) {
    if (blockedDaySet.has(ymd)) {
      // Unblock immediately
      setBlockSaving(true);
      try {
        await removeBlockedDay(ymd);
        setBlockedDays(prev => prev.filter(d => d.date !== ymd));
      } catch {
        setError('Failed to remove blocked day');
      } finally {
        setBlockSaving(false);
      }
    } else {
      // Open inline reason prompt
      setBlockingDate(ymd);
      setBlockReason('');
    }
  }

  async function confirmBlock() {
    if (!blockingDate) return;
    setBlockSaving(true);
    try {
      await setBlockedDay(blockingDate, blockReason.trim() || undefined);
      await listBlockedDays(year, month).then(setBlockedDays);
      setBlockingDate(null);
    } catch {
      setError('Failed to block day');
    } finally {
      setBlockSaving(false);
    }
  }

  const holidays = useMemo(() => irishPublicHolidays(year), [year]);

  const filteredBookings = useMemo(() => {
    return bookings.filter(b => {
      if (selectedRoomId !== 'all' && b.roomId !== selectedRoomId) return false;
      if (statusFilter === 'active' && !['pending', 'approved'].includes(b.status)) return false;
      if (statusFilter === 'approved' && b.status !== 'approved') return false;
      if (statusFilter === 'pending' && b.status !== 'pending') return false;
      const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      return b.startDate <= monthEnd && b.endDate >= monthStart;
    });
  }, [bookings, selectedRoomId, statusFilter, year, month]);

  const stats = useMemo(() => {
    const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    const monthBookings = bookings.filter(b => {
      if (selectedRoomId !== 'all' && b.roomId !== selectedRoomId) return false;
      return b.status !== 'cancelled' && b.startDate <= monthEnd && b.endDate >= monthStart;
    });

    const approvedCount = monthBookings.filter(b => b.status === 'approved').length;
    const totalNonCancelled = monthBookings.length;

    // Working days = Irish working days minus custom blocked days
    const baseWorkingDays = irishWorkingDaysInMonth(year, month);
    const blockedWorkingDays = blockedDays.filter(bd => {
      const d = parseYMD(bd.date);
      return isWeekday(d) && !holidays.has(bd.date);
    }).length;
    const workingDays = Math.max(0, baseWorkingDays - blockedWorkingDays);

    const roomCount = selectedRoomId === 'all' ? rooms.length : 1;
    const totalCapacity = workingDays * roomCount;

    const bookedSlots = bookings
      .filter(b => {
        if (selectedRoomId !== 'all' && b.roomId !== selectedRoomId) return false;
        return ['approved', 'pending'].includes(b.status) && b.startDate <= monthEnd && b.endDate >= monthStart;
      })
      .reduce((sum, b) => sum + workingDaysInRange(b.startDate, b.endDate, year, month), 0);

    const occupancy = totalCapacity > 0 ? Math.round((bookedSlots / totalCapacity) * 100) : 0;

    const totalAttendees = monthBookings.reduce((sum, b) => sum + (b.numberOfAttendees ?? 0), 0);
    const totalCustomers = monthBookings.reduce((sum, b) => sum + (b.numberOfCustomers ?? 0), 0);

    return { totalNonCancelled, approvedCount, workingDays, occupancy, totalAttendees, totalCustomers };
  }, [bookings, selectedRoomId, year, month, rooms, blockedDays, holidays]);

  const calendarDays = useMemo(() => buildCalendarGrid(year, month), [year, month]);

  const bookingsForDay = (d: Date): StudioBooking[] => {
    const ymd = toYMD(d);
    return filteredBookings.filter(b => b.startDate <= ymd && b.endDate >= ymd);
  };

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto p-8 font-display flex items-center justify-center">
        <Loader className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

      {/* Header */}
      <header className="flex items-center justify-between border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <CalendarRange size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Room Calendar</h1>
            <p className="text-slate-500 text-sm font-medium">Studio room booking overview — Dublin Co-Creation Studio</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-nowrap">
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
          >
            <Download size={16} /> Export CSV
          </button>

          <select
            value={selectedRoomId}
            onChange={e => setSelectedRoomId(e.target.value)}
            className="h-10 border border-[#e2e8f0] rounded-xl px-3 text-sm text-navy bg-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          >
            <option value="all">All rooms</option>
            {rooms.map(r => (
              <option key={r.roomId} value={r.roomId}>{r.name}</option>
            ))}
          </select>

          <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden bg-white h-10">
            {([
              ['active', 'Active'],
              ['approved', 'Approved'],
              ['pending', 'Pending'],
              ['all', 'All'],
            ] as [StatusFilterValue, string][]).map(([val, label]) => (
              <button
                key={val}
                onClick={() => setStatusFilter(val)}
                className={`px-3 text-xs font-semibold transition-all ${
                  statusFilter === val ? 'bg-primary text-white' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center border border-[#e2e8f0] rounded-xl overflow-hidden bg-white h-10">
            <button onClick={prevMonth} className="px-2 h-full text-slate-400 hover:text-navy hover:bg-slate-50 transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 text-sm font-semibold text-navy min-w-[130px] text-center">
              {MONTH_NAMES[month - 1]} {year}
            </span>
            <button onClick={nextMonth} className="px-2 h-full text-slate-400 hover:text-navy hover:bg-slate-50 transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)}><X className="w-4 h-4 text-red-400" /></button>
        </div>
      )}

      {/* Stats bar */}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl grid grid-cols-4 divide-x divide-[#e2e8f0]">
        <div className="px-6 py-4 text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Bookings this month</p>
          <p className="text-3xl font-bold text-navy">{stats.totalNonCancelled}</p>
        </div>
        <div className="px-6 py-4 text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Approved</p>
          <p className="text-3xl font-bold text-green-600">{stats.approvedCount}</p>
        </div>
        <div className="px-6 py-4 text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Attendees</p>
          <p className="text-3xl font-bold text-navy">{stats.totalAttendees}</p>
          <p className="text-xs text-slate-400 mt-0.5">{stats.totalCustomers} {stats.totalCustomers === 1 ? 'customer' : 'customers'}</p>
        </div>
        <div className="px-6 py-4 text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
            Occupancy
            <span className="ml-1 font-normal normal-case text-slate-300">({stats.workingDays} bookable days)</span>
          </p>
          <p className={`text-3xl font-bold ${
            stats.occupancy >= 80 ? 'text-green-600' :
            stats.occupancy >= 50 ? 'text-amber-600' :
            'text-navy'
          }`}>
            {stats.occupancy}%
          </p>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-[#e2e8f0]">
          {DAY_LABELS.map(d => (
            <div key={d} className="px-2 py-2.5 text-center text-xs font-bold uppercase tracking-wider text-slate-400">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {calendarDays.map((day, idx) => {
            const inMonth = day.getMonth() === month - 1;
            const ymd = toYMD(day);
            const isToday = ymd === toYMD(new Date());
            const isHoliday = holidays.has(ymd);
            const isBlocked = inMonth && blockedDaySet.has(ymd);
            const blockedDay = isBlocked ? blockedDays.find(d => d.date === ymd) : undefined;
            const dayBookings = bookingsForDay(day);
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;

            return (
              <div
                key={ymd}
                className={`min-h-[110px] p-1.5 border-r border-b border-[#e2e8f0] relative ${
                  !inMonth ? 'bg-slate-50/60' :
                  isBlocked ? 'bg-slate-100' :
                  isWeekend ? 'bg-slate-50/40' :
                  isHoliday ? 'bg-blue-50/30' :
                  'bg-white'
                } ${idx % 7 === 6 ? 'border-r-0' : ''}`}
              >
                <div className="flex items-center justify-between mb-1">
                  {/* Clickable day number — toggles block */}
                  <button
                    onClick={() => inMonth && handleDayClick(ymd)}
                    disabled={!inMonth || blockSaving}
                    className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                      isBlocked
                        ? 'bg-slate-400 text-white'
                        : isToday
                          ? 'bg-primary text-white'
                          : inMonth
                            ? 'text-navy hover:bg-slate-100'
                            : 'text-slate-300 cursor-default'
                    }`}
                    title={inMonth ? (isBlocked ? `Unblock this day${blockedDay?.reason ? ' — ' + blockedDay.reason : ''}` : 'Block this day') : undefined}
                  >
                    {day.getDate()}
                  </button>
                  <div className="flex items-center gap-1">
                    {isBlocked && <Ban className="w-3 h-3 text-slate-400" />}
                    {isHoliday && inMonth && !isBlocked && (
                      <span className="text-[10px] text-blue-500 font-medium">PH</span>
                    )}
                  </div>
                </div>

                {/* Inline block reason form */}
                {blockingDate === ymd && (
                  <div className="absolute inset-x-0 top-8 z-20 mx-1 bg-white border border-[#e2e8f0] rounded-xl shadow-lg p-2">
                    <p className="text-[10px] font-semibold text-navy mb-1">Block {ymd}</p>
                    <input
                      autoFocus
                      type="text"
                      placeholder="Reason (optional)"
                      value={blockReason}
                      onChange={e => setBlockReason(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { confirmBlock(); } else if (e.key === 'Escape') { setBlockingDate(null); } }}
                      className="w-full border border-[#e2e8f0] rounded-lg px-2 py-1 text-[10px] focus:ring-1 focus:ring-primary outline-none mb-1.5"
                    />
                    <div className="flex gap-1">
                      <button
                        onClick={confirmBlock}
                        disabled={blockSaving}
                        className="flex-1 py-1 bg-slate-700 text-white text-[10px] font-semibold rounded-lg hover:bg-slate-800 disabled:opacity-60"
                      >
                        {blockSaving ? '…' : 'Block'}
                      </button>
                      <button
                        onClick={() => setBlockingDate(null)}
                        className="flex-1 py-1 border border-[#e2e8f0] text-[10px] text-slate-500 rounded-lg hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {isBlocked ? (
                  <div className="mt-1">
                    {blockedDay?.reason && (
                      <p className="text-[10px] text-slate-500 truncate px-0.5">{blockedDay.reason}</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {dayBookings.slice(0, 3).map(b => (
                      <button
                        key={`${b.bookingId}-${ymd}`}
                        onClick={() => setSelectedBooking(b)}
                        className={`w-full text-left text-[10px] px-1.5 py-0.5 rounded font-medium truncate leading-relaxed ${CHIP_COLORS[b.status]}`}
                        title={`${b.customer} · ${DATE_TYPE_LABELS[b.dateType]}`}
                      >
                        {b.customer}
                      </button>
                    ))}
                    {dayBookings.length > 3 && (
                      <p className="text-[10px] text-slate-400 pl-1">+{dayBookings.length - 3} more</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-1 flex-wrap">
        {(['approved', 'pending', 'rejected', 'cancelled'] as BookingStatus[]).map(s => (
          <span key={s} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded border ${CHIP_COLORS[s]}`}>
            {STATUS_LABELS[s]}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded border bg-slate-100 text-slate-500 border-slate-200">
          <Ban className="w-3 h-3" /> Blocked
        </span>
        <span className="text-xs text-slate-400 ml-auto">Click a day number to block/unblock · PH = Irish public holiday</span>
      </div>

      {selectedBooking && (
        <BookingModal booking={selectedBooking} onClose={() => setSelectedBooking(null)} />
      )}

      {showExportModal && (
        <ExportCsvModal
          bookings={bookings}
          rooms={rooms}
          defaultFrom={`${year}-${String(month).padStart(2, '0')}-01`}
          defaultTo={`${year}-${String(month).padStart(2, '0')}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`}
          onClose={() => setShowExportModal(false)}
        />
      )}

      </div>
    </div>
  );
}
